import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ApiError, apiGet } from '../lib/api';

type ApiState<T> = {
  data: T | null;
  error: Error | null;
  loading: boolean;
  refetch: () => void;
  // Mutate the local cache without hitting the network. Used for optimistic
  // updates — pair with refetch() on success or another mutate() on error.
  mutate: (updater: (prev: T | null) => T | null) => void;
};

export function useApi<T>(path: string | null): ApiState<T> {
  const { getToken, previewUserId, notifyAuthFailure } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [tick, setTick] = useState(0);
  const reqRef = useRef(0);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  const mutate = useCallback((updater: (prev: T | null) => T | null) => {
    setData(updater);
  }, []);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    const id = ++reqRef.current;
    setLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const result = await apiGet<T>(path, token, previewUserId);
        if (id === reqRef.current) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (id === reqRef.current) {
          setError(err as Error);
          setData(null);
        }
        // 401 = token rejected by API. Drop the user back to SignIn instead
        // of leaving a blank screen with no recovery path.
        if (err instanceof ApiError && err.status === 401) {
          notifyAuthFailure();
        }
      } finally {
        if (id === reqRef.current) setLoading(false);
      }
    })();
    return () => {
      // Stale request — bump id so the in-flight callback won't update state.
      reqRef.current += 1;
    };
    // previewUserId in deps so toggling "view as" refetches everything.
  }, [path, getToken, previewUserId, notifyAuthFailure, tick]);

  return { data, error, loading, refetch, mutate };
}
